import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import LoginForm from '../components/LoginForm';
import StaffDashboard from '../components/StaffDashboard';
import AdminDashboard from '../components/AdminDashboard';
import ManagerDashboard from '../components/ManagerDashboard';
import PasswordCreation from '../components/PasswordCreation';
import { MFAVerifyStep } from '../components/MFAVerifyStep';
import { MFARequiredGate } from '../components/MFARequiredGate';
import IdleTimeoutWarning from '../components/IdleTimeoutWarning';

import { PilotStatusBanner } from '../components/billing/PilotStatusBanner';
import LicenseGate from '../components/billing/LicenseGate';
import { Button } from '@/components/ui/button';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { supabase } from '@/integrations/supabase/client';
import { ROLES } from '@/constants/roles';


import cimfoniLogo from '@/assets/cimfoni_logo_transparent.png';

const Index = () => {
  const { isAuthenticated, userRole, needsPasswordReset, setNeedsPasswordReset, mfaPending, completeMFA, logout, isLoading, profileLoadError } = useAuth();
  const [userEmail, setUserEmail] = React.useState<string>('');
  const [mfaGateSatisfied, setMfaGateSatisfied] = React.useState(false);

  // Reset the enforcement gate whenever the auth session changes
  React.useEffect(() => {
    if (!isAuthenticated) setMfaGateSatisfied(false);
  }, [isAuthenticated]);

  const { showWarning, remainingSeconds, stayActive } = useIdleTimeout({
    timeoutMs: 30 * 60 * 1000, // 30 minutes
    onIdle: logout,
    enabled: isAuthenticated,
  });

  React.useEffect(() => {
    const getUserEmail = async () => {
      const { data: { user: supabaseUser } } = await supabase.auth.getUser();
      if (supabaseUser?.email) {
        setUserEmail(supabaseUser.email);
      }
    };
    
    if (isAuthenticated && needsPasswordReset) {
      getUserEmail();
    }
  }, [isAuthenticated, needsPasswordReset]);

  // After a successful sign-in, honor any saved post-login redirect
  // (e.g. the OAuth consent page bounced the user through login).
  React.useEffect(() => {
    if (!isAuthenticated || needsPasswordReset || mfaPending) return;
    const target = sessionStorage.getItem('postLoginRedirect');
    if (!target) return;
    sessionStorage.removeItem('postLoginRedirect');
    if (target.startsWith('/') && !target.startsWith('//')) {
      window.location.replace(target);
    }
  }, [isAuthenticated, needsPasswordReset, mfaPending]);

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <img
          src={cimfoniLogo}
          alt="Loading"
          className="h-16 w-16 animate-spin opacity-80"
          style={{ animationDuration: '1.6s' }}
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  if (profileLoadError || !userRole) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md space-y-4 text-center">
          <img
            src={cimfoniLogo}
            alt="Cimfoni"
            className="mx-auto h-16 w-16 object-contain opacity-90"
            width={64}
            height={64}
          />
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-foreground">Account access is still being prepared</h1>
            <p className="text-sm text-muted-foreground">
              {profileLoadError || 'Your role has not loaded yet. Please sign in again or contact your administrator.'}
            </p>
          </div>
          <Button type="button" onClick={logout} className="w-full">
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (mfaPending) {
    return <MFAVerifyStep onVerified={completeMFA} onCancel={logout} />;
  }

  // Server-enforced MFA gate: when the org-wide toggle is on, every user must
  // enroll a TOTP factor and reach aal2 before entering the app.
  if (!mfaGateSatisfied) {
    return <MFARequiredGate onSatisfied={() => setMfaGateSatisfied(true)} />;
  }

  if (needsPasswordReset && userEmail) {
    return <PasswordCreation email={userEmail} onComplete={() => setNeedsPasswordReset(false)} />;
  }

  const dashboard = (() => {
    switch (userRole) {
      case ROLES.SUPER_ADMIN:
        return <AdminDashboard />;
      // Unified Manager UI for all manager-tier roles.
      // Per-role tab visibility is controlled by feature_toggles (managed
      // by Super Admin / Program Director from sidebar → Feature Toggles).
      case ROLES.PROGRAM_DIRECTOR:
      case ROLES.MANAGER:
      case ROLES.SUPERVISOR:
      case ROLES.CASE_MANAGER:
        return <ManagerDashboard />;
      case ROLES.RESIDENTIAL_AIDE:
      case ROLES.STAFF:
      default:
        return <StaffDashboard />;
    }
  })();

  return (
    <>
      <PilotStatusBanner />

      <LicenseGate>{dashboard}</LicenseGate>
      <IdleTimeoutWarning
        open={showWarning}
        remainingSeconds={remainingSeconds}
        onStayActive={stayActive}
      />
    </>
  );

};

export default Index;
